import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  Info,
  Sparkles,
  Filter,
  Settings,
} from "lucide-react";
import { isParameterDisabled } from "@/config/parameterDependencies";
import { AdvancedRangeSlider } from "@/components/ui/advanced-range-slider";
import { PinnableRangeSlider } from "@/components/ui/pinnable-range-slider";
import { Switch } from "@/components/ui/switch";
import { ThreeStateToggle, type ThreeStateValue } from "@/components/ui/three-state-toggle";
import { Label } from "@/components/ui/label";
import {
  GlassCard,
  GlassCardContent,
  GlassCardDescription,
  GlassCardHeader,
  GlassCardTitle,
} from "@/components/ui/glass-card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ParameterSection {
  title: string;
  icon: React.ReactNode;
  description: string;
  parameters: ParameterConfig[];
}

interface ParameterConfig {
  key: string;
  label: string;
  type: "range" | "boolean" | "select";
  description?: string;
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: any;
  choices?: string[];
  histogram?: number[];
  pinnable?: boolean;
}

const parameterSections: ParameterSection[] = [
  {
    title: "General Settings",
    icon: <Sparkles className="w-5 h-5" />,
    description: "Core algorithm configuration and general parameters",
    parameters: [
      {
        key: "neighborscount",
        label: "Neighbors Count",
        type: "range",
        description: "Number of neighbors for KNN classification",
        min: 5,
        max: 200,
        step: 1,
        defaultValue: [5, 200],
        pinnable: true,
      },
      {
        key: "maxbarsback",
        label: "Max Bars Back",
        type: "range",
        description: "Historical data lookback period",
        min: 500,
        max: 40000,
        step: 50,
        defaultValue: [1000, 8000],
        pinnable: true,
      },
      {
        key: "usedynamicexits",
        label: "Use Dynamic Exits",
        type: "boolean",
        description: "Enable dynamic exit strategies",
        defaultValue: false,
        pinnable: true,
      },
    ],
  },
  {
    title: "Filters Settings",
    icon: <Filter className="w-5 h-5" />,
    description: "Market condition filters to improve signal quality",
    parameters: [
      {
        key: "useemafilter",
        label: "Use EMA Filter",
        type: "boolean",
        description: "Filter trades based on EMA",
        defaultValue: false,
        pinnable: true,
      },
      {
        key: "emaperiod",
        label: "EMA Period",
        type: "range",
        description: "Exponential Moving Average period",
        min: 10,
        max: 600,
        step: 10,
        defaultValue: [10, 600],
        pinnable: true,
      },
      {
        key: "usesmafilter",
        label: "Use SMA Filter",
        type: "boolean",
        description: "Filter trades based on SMA",
        defaultValue: true,
        pinnable: true,
      },
      {
        key: "smaperiod",
        label: "SMA Period",
        type: "range",
        description: "Simple Moving Average period",
        min: 20,
        max: 600,
        step: 10,
        defaultValue: [20, 600],
        pinnable: true,
      },
      {
        key: "usevolatilityfilter",
        label: "Use Volatility Filter",
        type: "boolean",
        description: "Filter trades based on volatility",
        defaultValue: false,
        pinnable: true,
      },
      {
        key: "useregimefilter",
        label: "Use Regime Filter",
        type: "boolean",
        description: "Filter trades based on market regime",
        defaultValue: true,
        pinnable: true,
      },
      {
        key: "regimethreshold",
        label: "Regime Threshold",
        type: "range",
        description: "Threshold for regime detection",
        min: -2.5,
        max: 2.5,
        step: 0.05,
        defaultValue: [-2.5, 2.5],
        pinnable: true,
      },
      {
        key: "useadxfilter",
        label: "Use ADX Filter",
        type: "boolean",
        description: "Filter trades based on ADX indicator",
        defaultValue: false,
        pinnable: true,
      },
      {
        key: "adxthreshold",
        label: "ADX Threshold",
        type: "range",
        description: "Threshold for ADX filter",
        min: 20,
        max: 40,
        step: 1,
        defaultValue: [20, 40],
        pinnable: true,
      },
    ],
  },
  {
    title: "Kernel Settings",
    icon: <Settings className="w-5 h-5" />,
    description: "Advanced kernel regression parameters",
    parameters: [
      {
        key: "lookbackwindow",
        label: "Lookback Window",
        type: "range",
        description: "Window size for pattern recognition",
        min: 3,
        max: 20,
        step: 1,
        defaultValue: [3, 20],
        pinnable: true,
      },
      {
        key: "relativeweight",
        label: "Relative Weighting",
        type: "range",
        description: "Weight factor for calculations",
        min: 1,
        max: 50,
        step: 0.5,
        defaultValue: [1, 50],
        pinnable: true,
      },
      {
        key: "regressionlevel",
        label: "Regression Level",
        type: "range",
        description: "Level for regression calculations",
        min: 5,
        max: 75,
        step: 1,
        defaultValue: [5, 75],
        pinnable: true,
      },
      {
        key: "crossoverlag",
        label: "Lag",
        type: "range",
        description: "Lag for crossover detection",
        min: 2,
        max: 8,
        step: 1,
        defaultValue: [2, 8],
        pinnable: true,
      },
    ],
  },
];

interface ParameterConfigPanelProps {
  values: Record<string, any>;
  onChange: (key: string, value: any) => void;
  pinnedStates?: Record<string, boolean>;
  onPinnedChange?: (key: string, isPinned: boolean) => void;
}

export function ParameterConfigPanel({
  values,
  onChange,
  pinnedStates = {},
  onPinnedChange,
}: ParameterConfigPanelProps) {
  const [expandedSections, setExpandedSections] = React.useState<string[]>([
    "General Settings",
  ]);

  const toggleSection = (title: string) => {
    setExpandedSections((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title],
    );
  };

  // Helper to convert current state to three-state value
  const getThreeStateValue = (paramKey: string): ThreeStateValue => {
    const isPinned = pinnedStates[paramKey] || false;
    const boolValue = values[paramKey] ?? false;

    if (!isPinned) {
      return "optimize"; // Unpinned means optimize (test both)
    }
    return boolValue ? "on" : "off"; // Pinned to true or false
  };

  // Helper to handle three-state change
  const handleThreeStateChange = (paramKey: string, newState: ThreeStateValue) => {
    if (newState === "optimize") {
      // Unpinned - set default value false and unpinned
      onChange(paramKey, false);
      onPinnedChange?.(paramKey, false);
    } else if (newState === "off") {
      // Pinned to false
      onChange(paramKey, false);
      onPinnedChange?.(paramKey, true);
    } else {
      // "on" - Pinned to true
      onChange(paramKey, true);
      onPinnedChange?.(paramKey, true);
    }
  };

  const renderParameter = (param: ParameterConfig) => {
    // Calculate disabled state based on parameter dependencies
    const { disabled, reason } = isParameterDisabled(
      param.key,
      pinnedStates,
      values
    );

    switch (param.type) {
      case "range":
        if (param.pinnable && onPinnedChange) {
          return (
            <PinnableRangeSlider
              key={param.key}
              label={param.label}
              {...(param.description && { description: param.description })}
              {...(param.min !== undefined && { min: param.min })}
              {...(param.max !== undefined && { max: param.max })}
              {...(param.step !== undefined && { step: param.step })}
              value={values[param.key] || param.defaultValue}
              onValueChange={(value) => onChange(param.key, value)}
              isPinned={pinnedStates[param.key] || false}
              onPinnedChange={(pinned) => onPinnedChange(param.key, pinned)}
              disabled={disabled}
              disabledReason={reason}
              {...(param.histogram && { histogram: param.histogram })}
              {...(param.key === "relativeweight" && { unit: "x" })}
              {...(param.key === "regimethreshold" && { unit: "" })}
            />
          );
        } else {
          return (
            <AdvancedRangeSlider
              key={param.key}
              label={param.label}
              {...(param.description && { description: param.description })}
              {...(param.min !== undefined && { min: param.min })}
              {...(param.max !== undefined && { max: param.max })}
              {...(param.step !== undefined && { step: param.step })}
              value={values[param.key] || param.defaultValue}
              onValueChange={(value) => onChange(param.key, value)}
              disabled={disabled}
              {...(param.histogram && { histogram: param.histogram })}
              {...(param.key === "relativeweight" && { unit: "x" })}
              {...(param.key === "regimethreshold" && { unit: "" })}
            />
          );
        }
      case "boolean":
        if (param.pinnable && onPinnedChange) {
          return (
            <ThreeStateToggle
              key={param.key}
              id={param.key}
              label={param.label}
              {...(param.description && { description: param.description })}
              value={getThreeStateValue(param.key)}
              onChange={(newState) => handleThreeStateChange(param.key, newState)}
            />
          );
        } else {
          return (
            <div
              key={param.key}
              className="flex items-center justify-between py-2"
            >
              <div className="flex items-center gap-2">
                <Label htmlFor={param.key} className="text-sm font-medium">
                  {param.label}
                </Label>
                {param.description && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="w-4 h-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent className="bg-dark-800 text-white p-2 rounded-lg max-w-xs border border-dark-700">
                        <p className="text-sm">{param.description}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              <Switch
                id={param.key}
                checked={values[param.key] ?? param.defaultValue}
                onCheckedChange={(checked) => onChange(param.key, checked)}
              />
            </div>
          );
        }
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {parameterSections.map((section) => (
        <GlassCard key={section.title}>
          <GlassCardHeader
            className="cursor-pointer"
            onClick={() => toggleSection(section.title)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-r from-brand-blue/20 to-brand-blue-dark/20">
                  {section.icon}
                </div>
                <div>
                  <GlassCardTitle className="text-lg">
                    {section.title}
                  </GlassCardTitle>
                  <GlassCardDescription className="text-xs mt-1">
                    {section.description}
                  </GlassCardDescription>
                </div>
              </div>
              <motion.div
                animate={{
                  rotate: expandedSections.includes(section.title) ? 180 : 0,
                }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              </motion.div>
            </div>
          </GlassCardHeader>

          <AnimatePresence>
            {expandedSections.includes(section.title) && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                <GlassCardContent className="space-y-6">
                  {section.parameters.map(renderParameter)}
                </GlassCardContent>
              </motion.div>
            )}
          </AnimatePresence>
        </GlassCard>
      ))}
    </div>
  );
}
